// @ts-nocheck
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@store/MobiusOverrides/TextField/TextField";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import { StepFormValidationDictionary } from "@store//Library/Widgets/CreditForm/CreditFormContextContainer";
import useStepFormInputs from "@store/Library/Components/Bunzl/StepFormComponents/useStepFormInputs";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import cloneDeep from "lodash/cloneDeep";
import React from "react";
import { css } from "styled-components";
import StepFormTextDisplayValues from "./StepFormDisplayValues";

export interface StepFormCommonProps {
    hasSubmitted?: boolean;
    stepOverride?: number;
    inputKey: string;
    validation?: ((value: string) => string | undefined) | keyof StepFormValidationDictionary;
    error?: string;
    valueOverride?: string;
}
interface StepFormTextFieldProps extends Omit<TextFieldProps, "error">, StepFormCommonProps {
    variant?: keyof TextFieldVariantStyles;
}
interface TextFieldVariantStyles {
    medium: TextFieldPresentationProps;
    small: TextFieldPresentationProps;
    large: TextFieldPresentationProps;
    verySmall: TextFieldPresentationProps;
}

const baseTextFieldStyles: TextFieldPresentationProps = {
    labelProps: {
        size: 14,
        css: css`
            margin-top: 2px;
        `,
    },
    cssOverrides: {
        descriptionWrapper: css`
            > span {
                font-size: ${pxToRem(12)};
                font-weight: 400;
            }
        `,
        inputSelect: css`
            border-radius: 7px;
            font-size: ${pxToRem(14)};
            :focus {
                border: #007185 1px solid;
                padding: 0 ${pxToRem(10)};
                box-shadow: 0 0 0 3px #c8f3fa, 0 1px 2px rgba(15, 17, 17, 0.15) inset;
            }
            :disabled {
                border: none;
                padding: 0;
                background-color: inherit;
                cursor: pointer;
                color: black;
            }
        `,
        formInputWrapper: css`
            input {
                ::placeholder {
                    font-size: ${pxToRem(14)};
                    color: ${getColor("text.accent")};
                }
                font-size: ${pxToRem(14)};
            }
        `,
    },
};
export const textFieldVariantStyles: TextFieldVariantStyles = {
    large: {
        ...baseTextFieldStyles,
        cssOverrides: {
            ...baseTextFieldStyles.cssOverrides,
            formField: css`
                width: calc(100% - 1rem);
            `,
        },
    },
    medium: {
        ...baseTextFieldStyles,
        cssOverrides: {
            ...baseTextFieldStyles.cssOverrides,
            formField: css`
                width: calc(50% - 1rem);
            `,
        },
    },
    small: {
        ...baseTextFieldStyles,
        cssOverrides: {
            ...baseTextFieldStyles.cssOverrides,
            formField: css`
                width: calc(33% - 1rem);
            `,
        },
    },
    verySmall: {
        ...baseTextFieldStyles,
        cssOverrides: {
            ...baseTextFieldStyles.cssOverrides,
            formField: css`
                width: calc(22% - 1rem);
            `,
        },
    },
};

const StepFormTextField: React.FC<StepFormTextFieldProps> = ({
    hasSubmitted,
    inputKey,
    variant = "medium",
    disabled,
    stepOverride,
    validation,
    valueOverride,
    ...otherProps
}) => {
    const required = !!otherProps?.required;
    const { value, handleChange, editable, error } = useStepFormInputs({
        inputKey,
        required,
        validation,
        stepOverride,
        value: valueOverride,
    });

    const variantStyles = cloneDeep(textFieldVariantStyles[variant]);
    const internalHandleChange = (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event.target.value);

    return (
        <>
            {editable && (
                <TextField
                    onChange={internalHandleChange}
                    value={value}
                    mergeCss
                    {...variantStyles}
                    {...otherProps}
                    error={error}
                />
            )}

            {!editable && (
                <StepFormTextDisplayValues
                    label={typeof otherProps.label === "string" ? otherProps.label : ""}
                    value={value}
                    variant={variant}
                    error={error}
                />
            )}
        </>
    );
};

export default StepFormTextField;
